import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, Users, DollarSign, AlertTriangle, CheckCircle, 
  XCircle, Bell, RefreshCw, Eye, ChevronRight, Filter, Search,
  Mail, Phone, Building, User, ArrowUpDown, Loader2, CalendarClock,
  Ban, Check, AlertCircle, Play, History, FileText, Zap, Send
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { toast } from 'sonner';
import { formatET, getETAbbr } from '@/utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Timezone abbreviations mapping (matching frontend)
const timezoneAbbreviations = {
  'America/New_York': 'EST',
  'America/Chicago': 'CST',
  'America/Denver': 'MST',
  'America/Los_Angeles': 'PST',
  'America/Phoenix': 'MST',
  'America/Anchorage': 'AKST',
  'Pacific/Honolulu': 'HST',
  'America/Detroit': 'EST',
  'America/Indiana/Indianapolis': 'EST',
  'America/Kentucky/Louisville': 'EST',
  'America/Toronto': 'EST',
  'Europe/London': 'GMT',
  'Europe/Paris': 'CET',
  'Asia/Tokyo': 'JST',
  'Asia/Shanghai': 'CST',
  'Asia/Kolkata': 'IST',
  'Australia/Sydney': 'AEDT',
  'UTC': 'UTC',
};

// Helper to get timezone abbreviation
const getTimezoneAbbr = (timezone) => {
  if (!timezone) return 'EST';
  if (/^[A-Z]{2,5}$/.test(timezone)) return timezone;
  return timezoneAbbreviations[timezone] || timezone.split('/').pop().replace('_', ' ');
};

// Status badge configurations
const statusConfig = {
  ready: { 
    label: 'Ready to Go Live', 
    color: 'bg-emerald-500 text-white',
    icon: CheckCircle,
    bgColor: 'bg-emerald-50 border-emerald-200'
  },
  at_risk: { 
    label: 'At Risk', 
    color: 'bg-amber-500 text-white',
    icon: AlertTriangle,
    bgColor: 'bg-amber-50 border-amber-200'
  },
  action_required: { 
    label: 'Action Required', 
    color: 'bg-red-500 text-white',
    icon: AlertCircle,
    bgColor: 'bg-red-50 border-red-200'
  },
  confirmed: { 
    label: 'Confirmed', 
    color: 'bg-blue-500 text-white',
    icon: Check,
    bgColor: 'bg-blue-50 border-blue-200'
  },
  rescheduled: { 
    label: 'Rescheduled', 
    color: 'bg-purple-500 text-white',
    icon: CalendarClock,
    bgColor: 'bg-purple-50 border-purple-200'
  },
  cancelled: { 
    label: 'Cancelled', 
    color: 'bg-slate-500 text-white',
    icon: Ban,
    bgColor: 'bg-slate-50 border-slate-200'
  }
};

export default function AdminGoLivePage() {
  const [summary, setSummary] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Detail modal state
  const [selectedWebinar, setSelectedWebinar] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [webinarDetail, setWebinarDetail] = useState(null);
  
  // Action modals
  const [rescheduleModal, setRescheduleModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  
  // Automation state
  const [automationLogs, setAutomationLogs] = useState([]);
  const [dailySummaries, setDailySummaries] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [automationLoading, setAutomationLoading] = useState(false);

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/summary`);
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Fetch upcoming webinars
  const fetchWebinars = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/go-live/upcoming?sort_by=${sortBy}&limit=100`;
      if (filter !== 'all') {
        url += `&status_filter=${filter}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setWebinars(data);
      }
    } catch (error) {
      console.error('Error fetching webinars:', error);
      toast.error('Failed to fetch webinars');
    } finally {
      setLoading(false);
    }
  };

  // Fetch webinar detail
  const fetchWebinarDetail = async (webinarId) => {
    setDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/${webinarId}`);
      if (response.ok) {
        const data = await response.json();
        setWebinarDetail(data);
      }
    } catch (error) {
      console.error('Error fetching detail:', error);
      toast.error('Failed to fetch webinar details');
    } finally {
      setDetailLoading(false);
    }
  };

  // Actions
  const handleConfirmGoLive = async (webinarId) => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/${webinarId}/confirm`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Webinar confirmed for go-live');
        fetchWebinars();
        if (webinarDetail?.id === webinarId) {
          fetchWebinarDetail(webinarId);
        }
      }
    } catch (error) {
      toast.error('Failed to confirm webinar');
    }
  };

  const handleNotifySpeaker = async (webinarId) => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/${webinarId}/notify-speaker`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchWebinars();
      }
    } catch (error) {
      toast.error('Failed to notify speaker');
    }
  };

  const handleReschedule = async () => {
    if (!newDate || !selectedWebinar) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/${selectedWebinar.id}/reschedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_date: new Date(newDate).toISOString(),
          notify_customers: true
        })
      });
      if (response.ok) {
        toast.success('Webinar rescheduled successfully');
        setRescheduleModal(false);
        setNewDate('');
        fetchWebinars();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Failed to reschedule webinar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedWebinar) return;
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/${selectedWebinar.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: cancelReason,
          notify_customers: true,
          flag_for_refund: true
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Webinar cancelled. ${data.registrations_affected} registrations flagged for refund.`);
        setCancelModal(false);
        setCancelReason('');
        setSelectedWebinar(null);
        setWebinarDetail(null);
        fetchWebinars();
        fetchSummary();
      }
    } catch (error) {
      toast.error('Failed to cancel webinar');
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    fetchWebinars();
  }, []);

  useEffect(() => {
    fetchWebinars();
  }, [filter, sortBy]);

  // Fetch automation data when tab changes
  useEffect(() => {
    if (activeTab === 'automation') {
      fetchAutomationLogs();
      fetchDailySummaries();
    }
  }, [activeTab]);

  // Automation functions
  const fetchAutomationLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/automation/logs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setAutomationLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching automation logs:', error);
    }
  };

  const fetchDailySummaries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/daily-summaries?limit=7`);
      if (response.ok) {
        const data = await response.json();
        setDailySummaries(data.summaries || []);
      }
    } catch (error) {
      console.error('Error fetching daily summaries:', error);
    }
  };

  const fetchNotificationHistory = async (webinarId) => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/${webinarId}/notification-history`);
      if (response.ok) {
        const data = await response.json();
        setNotificationHistory(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notification history:', error);
    }
  };

  const runStatusUpdate = async () => {
    setAutomationLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/automation/run-status-update`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Status update completed. Processed ${data.results.total_processed} webinars.`);
        fetchAutomationLogs();
        fetchSummary();
        fetchWebinars();
      }
    } catch (error) {
      toast.error('Failed to run status update');
    } finally {
      setAutomationLoading(false);
    }
  };

  const runNotificationJob = async () => {
    setAutomationLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/automation/run-notifications`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        const results = data.results;
        toast.success(`Notifications sent: ${results['48_hour_confirmations_sent']} confirmations, ${results['24_hour_reminders_sent']} reminders`);
        fetchAutomationLogs();
      }
    } catch (error) {
      toast.error('Failed to run notification job');
    } finally {
      setAutomationLoading(false);
    }
  };

  const generateDailySummary = async () => {
    setAutomationLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/go-live/automation/generate-daily-summary`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Daily summary generated');
        fetchDailySummaries();
      }
    } catch (error) {
      toast.error('Failed to generate daily summary');
    } finally {
      setAutomationLoading(false);
    }
  };

  const sendSpeakerReminder = async (webinarId, reminderType) => {
    try {
      const response = await fetch(`${API_URL}/api/go-live/${webinarId}/send-speaker-reminder?reminder_type=${reminderType}`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        if (webinarDetail?.id === webinarId) {
          fetchWebinarDetail(webinarId);
        }
      }
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  // Filter webinars by search
  const filteredWebinars = webinars.filter(w => 
    w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.speaker_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.industry_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => formatET(dateStr, 'EEEE, MMMM dd, yyyy');

  const formatTime = (dateStr) => formatET(dateStr, 'h:mm a');

  // Short format for list view
  const formatDateShort = (dateStr) => formatET(dateStr, 'EEE, MMM dd');

  // Export webinars to CSV
  const exportToCSV = () => {
    if (webinars.length === 0) {
      toast.error('No webinars to export');
      return;
    }
    
    const headers = ['Title', 'Date', 'Time', 'Timezone', 'Status', 'Speaker', 'Industry', 'Registrations', 'Revenue'];
    const rows = webinars.map(w => [
      `"${w.title?.replace(/"/g, '""') || ''}"`,
      formatDate(w.scheduled_date),
      formatTime(w.scheduled_date),
      getETAbbr(w.scheduled_date),
      w.go_live_status || 'unknown',
      w.speaker_name || 'Unknown',
      w.industry || 'N/A',
      w.registration_count || 0,
      w.total_revenue || 0
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `go-live-webinars-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${webinars.length} webinars to CSV`);
  };

  // Bulk notify all at-risk speakers
  const bulkNotifyAtRiskSpeakers = async () => {
    const atRiskWebinars = webinars.filter(w => w.go_live_status === 'at_risk' && !w.speaker_notified);
    if (atRiskWebinars.length === 0) {
      toast.info('No at-risk webinars with unnotified speakers');
      return;
    }
    
    setAutomationLoading(true);
    let notified = 0;
    
    for (const webinar of atRiskWebinars) {
      try {
        const response = await fetch(`${API_URL}/api/go-live/${webinar.id}/notify-speaker`, {
          method: 'POST'
        });
        if (response.ok) notified++;
      } catch (error) {
        console.error('Failed to notify speaker for', webinar.id);
      }
    }
    
    toast.success(`Notified ${notified} speakers of at-risk webinars`);
    setAutomationLoading(false);
    fetchWebinars();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-go-live-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Go-Live Management</h1>
          <p className="text-slate-600">Manage upcoming webinar eligibility and scheduling</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportToCSV} variant="outline" size="sm">
            <FileText className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            onClick={bulkNotifyAtRiskSpeakers} 
            variant="outline" 
            size="sm"
            disabled={automationLoading}
          >
            {automationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
            Notify At-Risk
          </Button>
          <Button onClick={() => { fetchSummary(); fetchWebinars(); }} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="automation" className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Automation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-6">
          {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_upcoming}</p>
                <p className="text-sm text-slate-600">Total Upcoming</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{summary.ready_to_go}</p>
                <p className="text-sm text-slate-600">Ready to Go</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{summary.at_risk}</p>
                <p className="text-sm text-slate-600">At Risk</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{summary.action_required}</p>
                <p className="text-sm text-slate-600">Action Required</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{summary.total_registrations}</p>
                <p className="text-sm text-slate-600">Registrations</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(summary.total_revenue)}</p>
                <p className="text-sm text-slate-600">Expected Revenue</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by title, speaker, or industry..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select 
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="all">All Status</option>
                <option value="ready">Ready to Go</option>
                <option value="at_risk">At Risk</option>
                <option value="action_required">Action Required</option>
              </select>
            </div>
            
            <div className="flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4 text-slate-400" />
              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="date">Sort by Date</option>
                <option value="registrations">Sort by Registrations</option>
                <option value="revenue">Sort by Revenue</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Webinar List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Webinar Cards */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Upcoming Webinars ({filteredWebinars.length})</h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredWebinars.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No webinars found
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {filteredWebinars.map((webinar) => {
                const config = statusConfig[webinar.go_live_status] || statusConfig.at_risk;
                const StatusIcon = config.icon;
                const isSelected = selectedWebinar?.id === webinar.id;
                
                return (
                  <div 
                    key={webinar.id}
                    className={`bg-white rounded-xl border p-4 cursor-pointer transition-all hover:shadow-md ${
                      isSelected ? 'ring-2 ring-blue-500' : ''
                    } ${config.bgColor}`}
                    onClick={() => {
                      setSelectedWebinar(webinar);
                      fetchWebinarDetail(webinar.id);
                    }}
                    data-testid={`webinar-card-${webinar.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={config.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                          {webinar.hours_to_live <= 24 && (
                            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
                              {webinar.hours_to_live.toFixed(0)}h to live
                            </Badge>
                          )}
                        </div>
                        
                        <h3 className="font-semibold text-slate-900 truncate mb-1">
                          {webinar.title}
                        </h3>
                        
                        <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDateShort(webinar.scheduled_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(webinar.scheduled_date)} {getETAbbr(webinar.scheduled_date)}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-slate-600">
                            <User className="w-4 h-4" />
                            {webinar.speaker_name || 'No speaker'}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="text-slate-600">{webinar.industry_name || 'No industry'}</span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-lg font-bold text-slate-900">
                          <Users className="w-4 h-4" />
                          {webinar.registration_count}
                        </div>
                        <p className="text-sm text-slate-600">{formatCurrency(webinar.total_revenue)}</p>
                        <ChevronRight className="w-5 h-5 text-slate-400 mt-2 ml-auto" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail Panel */}
        <div className="bg-white rounded-xl border p-6">
          {!selectedWebinar ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-500 py-12">
              <Eye className="w-12 h-12 mb-4 text-slate-300" />
              <p>Select a webinar to view details</p>
            </div>
          ) : detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : webinarDetail ? (
            <div className="space-y-6">
              {/* Header */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {(() => {
                    const config = statusConfig[webinarDetail.go_live_status] || statusConfig.at_risk;
                    const StatusIcon = config.icon;
                    return (
                      <Badge className={config.color}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {config.label}
                      </Badge>
                    );
                  })()}
                </div>
                <h2 className="text-xl font-bold text-slate-900">{webinarDetail.title}</h2>
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">Scheduled Date</p>
                  <p className="font-semibold">{formatDate(webinarDetail.scheduled_date)}</p>
                  <p className="text-sm text-slate-600">{formatTime(webinarDetail.scheduled_date)} {getETAbbr(webinarDetail.scheduled_date)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm text-slate-600">Time to Live</p>
                  <p className="font-semibold text-lg">
                    {webinarDetail.hours_to_live > 24 
                      ? `${Math.floor(webinarDetail.hours_to_live / 24)} days ${Math.floor(webinarDetail.hours_to_live % 24)} hours`
                      : `${webinarDetail.hours_to_live.toFixed(0)} hours`
                    }
                  </p>
                </div>
              </div>

              {/* Speaker Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-slate-900 mb-2">Speaker</h3>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{webinarDetail.speaker_name || 'Unknown'}</p>
                    <p className="text-sm text-slate-600">{webinarDetail.speaker_email}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {webinarDetail.speaker_notified && (
                      <Badge className="bg-green-100 text-green-700">Notified</Badge>
                    )}
                    <div className="flex flex-col gap-1">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => sendSpeakerReminder(webinarDetail.id, '24_hour_reminder')}
                      >
                        <Bell className="w-3 h-3 mr-1" />
                        24h Reminder
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => sendSpeakerReminder(webinarDetail.id, 'confirmation_request')}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Confirm Request
                      </Button>
                    </div>
                  </div>
                </div>
                {webinarDetail.speaker_confirmed && (
                  <div className="mt-2 pt-2 border-t border-blue-100">
                    <Badge className="bg-emerald-100 text-emerald-700">
                      <Check className="w-3 h-3 mr-1" />
                      Speaker Confirmed
                    </Badge>
                  </div>
                )}
              </div>

              {/* Registration Stats */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{webinarDetail.registration_count}</p>
                  <p className="text-sm text-slate-600">Registrations</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{formatCurrency(webinarDetail.total_revenue)}</p>
                  <p className="text-sm text-slate-600">Revenue</p>
                </div>
              </div>

              {/* Customers List */}
              {webinarDetail.customers && webinarDetail.customers.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-900 mb-3">Registered Customers ({webinarDetail.customers.length})</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {webinarDetail.customers.map((customer, idx) => (
                      <div key={idx} className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{customer.customer_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-600">
                            <Mail className="w-3 h-3" />
                            {customer.customer_email}
                          </div>
                          {customer.customer_company && (
                            <div className="flex items-center gap-2 text-xs text-slate-600">
                              <Building className="w-3 h-3" />
                              {customer.customer_company}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm">{formatCurrency(customer.amount_paid)}</p>
                          <Badge variant="outline" className="text-xs">{customer.package_type}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                {webinarDetail.go_live_status !== 'confirmed' && (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleConfirmGoLive(webinarDetail.id)}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Confirm Go-Live
                  </Button>
                )}
                <Button 
                  variant="outline"
                  onClick={() => {
                    setRescheduleModal(true);
                  }}
                >
                  <CalendarClock className="w-4 h-4 mr-2" />
                  Reschedule
                </Button>
                <Button 
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => {
                    setCancelModal(true);
                  }}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
        </TabsContent>

        {/* Automation Tab */}
        <TabsContent value="automation" className="mt-6 space-y-6">
          {/* Automation Controls */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Automation Controls
            </h2>
            <p className="text-slate-600 mb-4">
              Run automated tasks manually or view scheduled job history. These jobs normally run automatically on a schedule.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Update Statuses</h3>
                <p className="text-sm text-slate-600 mb-3">Recalculate go-live status for all upcoming webinars based on registration count.</p>
                <Button 
                  onClick={runStatusUpdate} 
                  disabled={automationLoading}
                  className="w-full"
                >
                  {automationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                  Run Status Update
                </Button>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Send Notifications</h3>
                <p className="text-sm text-slate-600 mb-3">Send speaker reminders (48h confirmation, 24h reminder, 1h reminder).</p>
                <Button 
                  onClick={runNotificationJob} 
                  disabled={automationLoading}
                  className="w-full"
                >
                  {automationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Run Notifications
                </Button>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-2">Generate Summary</h3>
                <p className="text-sm text-slate-600 mb-3">Generate a daily summary report of all upcoming webinars.</p>
                <Button 
                  onClick={generateDailySummary} 
                  disabled={automationLoading}
                  className="w-full"
                >
                  {automationLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Generate Summary
                </Button>
              </div>
            </div>
          </div>

          {/* Daily Summaries */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-500" />
              Recent Daily Summaries
            </h2>
            {dailySummaries.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No daily summaries yet. Generate one above.</p>
            ) : (
              <div className="space-y-4">
                {dailySummaries.map((summary, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-slate-900">
                        {new Date(summary.report_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </span>
                      {summary.alerts?.length > 0 && (
                        <Badge className="bg-red-100 text-red-700">{summary.alerts.length} Alerts</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Tomorrow</span>
                        <p className="font-semibold">{summary.webinars_tomorrow} webinars</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Next 7 Days</span>
                        <p className="font-semibold">{summary.webinars_next_week} webinars</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Ready</span>
                        <p className="font-semibold text-emerald-600">{summary.status_breakdown?.ready || 0}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Action Required</span>
                        <p className="font-semibold text-red-600">{summary.status_breakdown?.action_required || 0}</p>
                      </div>
                    </div>
                    {summary.alerts?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        {summary.alerts.map((alert, alertIdx) => (
                          <div key={alertIdx} className={`flex items-center gap-2 text-sm ${alert.type === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                            <AlertCircle className="w-4 h-4" />
                            {alert.message}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Automation Logs */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-purple-500" />
              Automation Job History
            </h2>
            {automationLogs.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No automation logs yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {automationLogs.map((log, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        log.job_type === 'go_live_status_update' ? 'bg-blue-100' : 'bg-purple-100'
                      }`}>
                        {log.job_type === 'go_live_status_update' ? (
                          <RefreshCw className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Bell className="w-4 h-4 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 text-sm">
                          {log.job_type === 'go_live_status_update' ? 'Status Update' : 'Speaker Notifications'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(log.run_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      {log.job_type === 'go_live_status_update' ? (
                        <span className="text-slate-600">
                          Processed: {log.results?.total_processed || 0}
                        </span>
                      ) : (
                        <span className="text-slate-600">
                          Sent: {(log.results?.['48_hour_confirmations_sent'] || 0) + (log.results?.['24_hour_reminders_sent'] || 0)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reschedule Modal */}
      <Dialog open={rescheduleModal} onOpenChange={setRescheduleModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reschedule Webinar</DialogTitle>
            <DialogDescription>
              Select a new date and time for "{selectedWebinar?.title}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">New Date & Time</label>
            <Input
              type="datetime-local"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRescheduleModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleReschedule} disabled={!newDate || actionLoading}>
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reschedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={cancelModal} onOpenChange={setCancelModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Cancel Webinar</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel "{selectedWebinar?.title}"? 
              {selectedWebinar?.registration_count > 0 && (
                <span className="block mt-2 font-medium text-amber-600">
                  {selectedWebinar.registration_count} registrations will be flagged for refund.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">Cancellation Reason (optional)</label>
            <Input
              placeholder="Enter reason..."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelModal(false)}>
              Keep Webinar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={actionLoading}
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Cancel Webinar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
